import { useMemo, useState } from "react";
import {
    Box,
    Breadcrumbs,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormHelperText,
    FormLabel,
    Input,
    Link,
    Typography,
} from "@mui/joy";
import { ArrowLeft, ChevronRight, ExternalLink } from "lucide-react";
import {
    Link as RouterLink,
    Navigate,
    useNavigate,
    useParams,
} from "react-router-dom";

import { Button, Alert } from "../../shared";
import { useAuth, useNotification, useApiResponseParser } from "../../hooks";
import { useIntegration } from "../../hooks/query";
import {
    getIntegrationById,
    isOrganizationIntegrationActive,
    type IntegrationStatus,
} from "./integrations";

function StatusChip({ status }: { status: IntegrationStatus }) {
    if (status === "connected") {
        return (
            <Chip size="sm" variant="soft" color="success">
                Connected
            </Chip>
        );
    }
    return (
        <Chip size="sm" variant="soft" color="neutral">
            Not configured
        </Chip>
    );
}

function Breadcrumb({ name }: { name: string }) {
    return (
        <Box sx={{ mt: 0, ml: 1, mr: 2 }}>
            <Link
                component={RouterLink}
                to="/admin/integrations"
                level="body-xs"
                startDecorator={<ArrowLeft size={12} />}
                sx={{
                    mb: 0.5,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.25,
                    fontWeight: 500,
                    textDecoration: "none",
                    color: "text.secondary",
                    "&:hover": { color: "text.primary", textDecoration: "none" },
                }}
            >
                Back to integrations
            </Link>
            <Breadcrumbs separator={<ChevronRight size={16} />} sx={{ "--Breadcrumbs-gap": "6px" }}>
                <Link
                    component={RouterLink}
                    to="/admin"
                    level="body-sm"
                    sx={{
                        fontWeight: 500,
                        textDecoration: "none",
                        color: "inherit",
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    Admin
                </Link>
                <Link
                    component={RouterLink}
                    to="/admin/integrations"
                    level="body-sm"
                    sx={{
                        fontWeight: 500,
                        textDecoration: "none",
                        color: "inherit",
                        "&:hover": { textDecoration: "underline" },
                    }}
                >
                    Integrations
                </Link>
                <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                    {name}
                </Typography>
            </Breadcrumbs>
        </Box>
    );
}

function buildCredentials(
    fieldKeys: string[],
    values: Record<string, string>,
): Record<string, string> {
    return Object.fromEntries(fieldKeys.map((key) => [key, values[key]?.trim() ?? ""]));
}

function credentialsComplete(
    fieldKeys: string[],
    credentials: Record<string, string>,
): boolean {
    return fieldKeys.every((key) => credentials[key]?.length > 0);
}

export default function ConfigureIntegration() {
    const navigate = useNavigate();
    const { integrationId } = useParams<{ integrationId: string }>();
    const integration = getIntegrationById(integrationId);
    const { selectedOrganization } = useAuth();
    const { showSuccess, showError } = useNotification();
    const { parseErrorResponseData } = useApiResponseParser();

    const { useList, useCreateSecret, useUpdateSecret, useDeleteSecret, useValidate } =
        useIntegration();
    const organizationId = selectedOrganization?.Id;
    const { data: integrationRows, isLoading: listLoading } = useList(organizationId);

    const createSecret = useCreateSecret();
    const updateSecret = useUpdateSecret();
    const deleteSecret = useDeleteSecret();
    const validateIntegration = useValidate();

    const [values, setValues] = useState<Record<string, string>>(() =>
        Object.fromEntries(
            (integration?.fields || []).map((field) => [field.key, ""]),
        ),
    );

    const isConfigured = useMemo(() => {
        if (!integration || !integrationRows) return false;
        const row = integrationRows.find((r) => r.IntegrationId === integration.id);
        return row ? isOrganizationIntegrationActive(row.IsActive) : false;
    }, [integration, integrationRows]);

    const status: IntegrationStatus = isConfigured ? "connected" : "not_configured";

    const fieldKeys = useMemo(
        () => (integration?.fields || []).map((field) => field.key),
        [integration],
    );

    const isBusy =
        createSecret.isPending ||
        updateSecret.isPending ||
        deleteSecret.isPending ||
        validateIntegration.isPending;

    if (!integration) {
        return <Navigate to="/admin/integrations" replace />;
    }

    if (!organizationId) {
        return (
            <Box flex={1} p={3}>
                <Alert errorMessage="Select an organization to configure integrations." />
            </Box>
        );
    }

    if (listLoading) {
        return (
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <CircularProgress />
            </Box>
        );
    }

    const handleSave = async () => {
        const credentials = buildCredentials(fieldKeys, values);
        if (!credentialsComplete(fieldKeys, credentials)) {
            showError("Fill in all credential fields before saving.");
            return;
        }

        const payload = {
            OrganizationId: organizationId,
            IntegrationId: integration.id,
            Credentials: credentials,
        };

        try {
            if (integration.id === "eagleview") {
                await validateIntegration.mutateAsync({
                    OrganizationId: organizationId,
                    Provider: "eagleview",
                    Credentials: {
                        clientId: credentials.clientId,
                        clientSecret: credentials.clientSecret,
                    },
                });
            }

            if (isConfigured) {
                await updateSecret.mutateAsync(payload);
            } else {
                await createSecret.mutateAsync(payload);
            }

            showSuccess(`${integration.name} configuration saved.`);
            navigate("/admin/integrations");
        } catch (error) {
            showError(parseErrorResponseData(error) || "Failed to save integration.");
        }
    };

    const handleTestConnection = async () => {
        if (integration.id !== "eagleview") {
            showError("Connection test is only available for EagleView.");
            return;
        }

        const credentials = buildCredentials(fieldKeys, values);
        if (!credentials.clientId || !credentials.clientSecret) {
            showError("Enter Client ID and Client Secret to test the connection.");
            return;
        }

        try {
            await validateIntegration.mutateAsync({
                OrganizationId: organizationId,
                Provider: "eagleview",
                Credentials: {
                    clientId: credentials.clientId,
                    clientSecret: credentials.clientSecret,
                },
            });
            showSuccess("EagleView credentials verified.");
        } catch (error) {
            showError(parseErrorResponseData(error) || "Connection test failed.");
        }
    };

    const handleRemove = async () => {
        if (!isConfigured) {
            navigate("/admin/integrations");
            return;
        }

        const confirmed = window.confirm(
            `Remove ${integration.name} credentials for ${selectedOrganization?.Name}? This cannot be undone.`,
        );
        if (!confirmed) return;

        try {
            await deleteSecret.mutateAsync({
                OrganizationId: organizationId,
                IntegrationId: integration.id,
            });
            showSuccess(`${integration.name} configuration removed.`);
            navigate("/admin/integrations");
        } catch (error) {
            showError(parseErrorResponseData(error) || "Failed to remove integration.");
        }
    };

    return (
        <Box
            flex={1}
            display="flex"
            flexDirection="column"
            maxHeight="100%"
            sx={{ overflow: "hidden", minWidth: 0, minHeight: 0 }}
        >
            <Breadcrumb name={integration.name} />
            <Divider sx={{ mt: 0, ml: 0 }} />

            <Box flex={1} sx={{ minWidth: 0, overflow: "auto" }}>
                <Box
                    sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 1, maxWidth: 700 }}
                >
                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "4px",
                                display: "grid",
                                placeItems: "center",
                                bgcolor: "background.level1",
                                flexShrink: 0,
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                component="img"
                                src={integration.icon}
                                alt=""
                                sx={{
                                    width: 40,
                                    height: 40,
                                    objectFit: "contain",
                                }}
                            />
                        </Box>
                        <Box flex={1} minWidth={0}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                flexWrap="wrap"
                                mb={0.5}
                            >
                                <Typography level="title-lg">
                                    {integration.name}
                                </Typography>
                                <StatusChip status={status} />
                            </Box>
                            <Typography
                                level="body-sm"
                                sx={{ color: "text.secondary" }}
                            >
                                {integration.description}
                            </Typography>
                            <Typography
                                level="body-xs"
                                sx={{ color: "text.tertiary", mt: 0.5 }}
                            >
                                {integration.category}
                            </Typography>
                            {integration.docsUrl ? (
                                <Link
                                    href={integration.docsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    level="body-sm"
                                    endDecorator={<ExternalLink size={14} />}
                                    sx={{
                                        mt: 1,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        fontWeight: 500,
                                    }}
                                >
                                    View provider documentation
                                </Link>
                            ) : null}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 700 }}>
                    <Divider sx={{ my: 2 }} />

                    <Typography level="title-md" sx={{ mb: 0.5 }}>
                        Credentials
                    </Typography>
                    <Typography
                        level="body-sm"
                        sx={{ color: "text.secondary", mb: 2 }}
                    >
                        Provide API credentials for {integration.name} for{" "}
                        {selectedOrganization?.Name}. Saved values are stored
                        securely and are not shown again on this screen.
                    </Typography>

                    <Box display="flex" flexDirection="column" gap={1.75}>
                        {integration.fields.map((field) => (
                            <FormControl key={field.key}>
                                <FormLabel>{field.label}</FormLabel>
                                <FormHelperText sx={{ mt: -0.5, mb: 0.75 }}>
                                    {field.helperText}
                                </FormHelperText>
                                <Input
                                    type={field.secret ? "password" : "text"}
                                    value={values[field.key] || ""}
                                    placeholder={field.placeholder}
                                    disabled={isBusy}
                                    onChange={(event) =>
                                        setValues((prev) => ({
                                            ...prev,
                                            [field.key]: event.target.value,
                                        }))
                                    }
                                    sx={{ borderRadius: "4px" }}
                                />
                            </FormControl>
                        ))}
                    </Box>

                    {isConfigured ? (
                        <Typography
                            level="body-xs"
                            sx={{ color: "text.tertiary", mt: 2 }}
                        >
                            Re-enter all fields to replace stored credentials.
                        </Typography>
                    ) : null}

                    <Box
                        display="flex"
                        gap={1.25}
                        flexWrap="wrap"
                        alignItems="center"
                        sx={{ mt: 2.5 }}
                    >
                        <Button
                            variant="solid"
                            color="primary"
                            loading={isBusy}
                            onClick={handleSave}
                        >
                            {isConfigured ? "Update configuration" : "Save configuration"}
                        </Button>
                        <Button
                            variant="outlined"
                            color="neutral"
                            disabled={isBusy}
                            onClick={() => navigate("/admin/integrations")}
                        >
                            Cancel
                        </Button>
                        {integration.id === "eagleview" ? (
                            <Button
                                variant="outlined"
                                color="neutral"
                                loading={validateIntegration.isPending}
                                disabled={isBusy && !validateIntegration.isPending}
                                onClick={handleTestConnection}
                                sx={{ ml: "auto" }}
                            >
                                Test connection
                            </Button>
                        ) : null}
                    </Box>

                    {isConfigured ? (
                        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                            <Button
                                variant="plain"
                                color="danger"
                                loading={deleteSecret.isPending}
                                disabled={isBusy && !deleteSecret.isPending}
                                onClick={handleRemove}
                            >
                                Remove configuration
                            </Button>
                        </Box>
                    ) : null}
                </Box>
            </Box>
        </Box>
    );
}
